import React, { useState } from 'react';
import { CreditCard, CheckCircle2, Building2, ArrowRight, Loader2, Landmark, Globe } from 'lucide-react';
import { Button } from '../ui/button';
import { payouts } from '../../mock';
import { usd, nok } from '../../lib/format';
import { API } from '../../lib/api';
import { toast } from 'sonner';

export default function PayoutsTab() {
  const [connected, setConnected] = useState(payouts.connected);
  const [connecting, setConnecting] = useState(false);

  const openOnboarding = async () => {
    try {
      const { data } = await API.get('/stripe/onboarding-url');
      if (data.onboarding_url) window.open(data.onboarding_url, '_blank');
      else toast('Onboarding link unavailable');
    } catch { toast.error('Could not open Stripe onboarding'); }
  };

  const connect = async () => {
    setConnecting(true);
    await openOnboarding();
    setTimeout(() => { setConnecting(false); setConnected(true); toast.success('Stripe Connect linked — payouts enabled'); }, 1200);
  };

  return (
    <div className="space-y-6">
      {/* Stripe Connect card */}
      {!connected ? (
        <div className="glass relative overflow-hidden rounded-2xl p-6">
          <div className="pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full bg-[#635bff]/20 blur-2xl" />
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#635bff]/20">
              <CreditCard className="h-5 w-5 text-[#8a83ff]" />
            </div>
            <div>
              <h3 className="font-display text-base font-semibold text-white">Connect with Stripe</h3>
              <p className="text-xs text-muted-foreground">Get paid securely with Stripe Connect</p>
            </div>
          </div>
          <p className="mt-4 text-sm text-white/70">Link your Stripe account to start receiving payouts. Onboarding takes about 2 minutes.</p>
          <Button onClick={connect} disabled={connecting} className="mt-4 rounded-xl bg-[#635bff] font-semibold text-white hover:bg-[#5249e0]">
            {connecting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Connecting...</> : <>Connect with Stripe <ArrowRight className="ml-1.5 h-4 w-4" /></>}
          </Button>
        </div>
      ) : (
        <div className="glass rounded-2xl p-6">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/15">
              <CheckCircle2 className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h3 className="font-display text-base font-semibold text-white">{payouts.provider}</h3>
                <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-semibold text-primary">CONNECTED</span>
              </div>
              <p className="text-xs text-muted-foreground">Payouts active · settled in {payouts.payoutCurrency}</p>
            </div>
            <Button onClick={() => toast('Opening Stripe dashboard...')} variant="ghost" size="sm" className="rounded-full text-muted-foreground hover:text-white">Manage</Button>
          </div>

          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
              <p className="text-xs text-muted-foreground">Available balance</p>
              <p className="font-display text-2xl font-bold text-white">{usd(payouts.balanceUSD)}</p>
              <p className="text-xs text-primary">≈ {nok(payouts.balanceUSD * payouts.fxRate)}</p>
            </div>
            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
              <p className="text-xs text-muted-foreground">Pending</p>
              <p className="font-display text-2xl font-bold text-white">{usd(payouts.pendingUSD)}</p>
              <p className="text-xs text-muted-foreground">Next payout {payouts.nextPayout}</p>
            </div>
          </div>

          <div className="mt-3 flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-4">
            <Landmark className="h-5 w-5 text-muted-foreground" />
            <div className="flex-1">
              <p className="text-sm text-white">{payouts.bank.name} •••• {payouts.bank.last4}</p>
              <p className="flex items-center gap-1 text-xs text-muted-foreground"><Globe className="h-3 w-3" /> {payouts.bank.country} · Payouts in {payouts.payoutCurrency}</p>
            </div>
            <Button onClick={() => toast('Payout initiated')} size="sm" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">Pay out now</Button>
          </div>
        </div>
      )}

      {/* Payout history */}
      {connected && (
        <div className="glass rounded-2xl p-5">
          <h3 className="mb-3 font-display text-sm font-semibold text-white">Payout history</h3>
          <div className="space-y-1">
            {payouts.history.map((h) => (
              <div key={h.id} className="flex items-center justify-between rounded-lg px-2 py-2.5 text-sm hover:bg-white/[0.03]">
                <span className="text-white/80">{h.date}</span>
                <span className="text-muted-foreground">{usd(h.usd)}</span>
                <span className="font-medium text-white">{nok(h.nok)}</span>
                <span className="rounded-full bg-primary/15 px-2 py-0.5 text-xs font-medium text-primary">{h.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
